/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React, { useState, useEffect, useRef, useMemo } from 'react';
import Select, { GroupBase, SelectInstance, StylesConfig } from 'react-select';
import Abilities from 'model/abilities/Abilities';
import MarkdownText from 'components/common/MarkdownText';
import FeatService from 'services/FeatService';

interface AbilitySelectorProps {
  blockType: string;
  isOpen: boolean;
  onSelect: (selectedOption: Ability) => void;
  onCancel: () => void;
}

const AbilitySelector: React.FC<AbilitySelectorProps> = ({ blockType, isOpen, onSelect, onCancel }) => {
  const selectRef = useRef<SelectInstance<Ability, false>>(null);
  const [options, setOptions] = useState<Ability[]>([]);
  const [selectedOption, setSelectedOption] = useState<Ability | null>(null);
  const [selectedDesc, setSelectedDesc] = useState('No description available.');
  const [menuIsOpen, setMenuIsOpen] = useState(true); // Control for dropdown open/close state

  const getFeats = async(options: Ability[]) => {
    const feats = await FeatService.fetchFeats(false, () => {});
    setOptions([...options, ...feats]);
  }
  useEffect(() => {
    const fetchedOptions = Abilities.getOptions(blockType);
    if (blockType === 'traits') {
      getFeats(fetchedOptions);
    } else {
      setOptions(fetchedOptions);
    }
  }, [blockType]);

  // Group and sort options
  const groupedAndSortedOptions: GroupBase<Ability>[] = useMemo(() => {
    if (options.length === 0) return [];
    
    const groupedOptions = options.reduce<Record<string, Ability[]>>((acc, option) => {
      if (option.group) {
        acc[option.group] = acc[option.group] || [];
        acc[option.group].push(option);
      }
      return acc;
    }, {});

    return Object.keys(groupedOptions).map(source => ({
      label: source,
      options: groupedOptions[source].sort((a, b) => (a.label ?? a.name).localeCompare((b.label ?? b.name)))
                                     .map((option) => ({
        // No 'value' field is present in Ability, so we'll use 'name' as the unique identifier
        label: option.label || option.name, // Fallback to name if label is missing
        name: option.name,
        desc: option.desc,
        group: option.group,
      }))
    }));
  }, [options]);

  // When options change, reset selected option and description
  useEffect(() => {
    const defaultOption = groupedAndSortedOptions[0]?.options[0] || null;
    setSelectedOption(defaultOption);
    setSelectedDesc(defaultOption?.desc || 'No description available.');
  }, [groupedAndSortedOptions]);

  // Open the dropdown when the component is opened
  useEffect(() => {
    if (isOpen && selectRef.current) {
      selectRef.current.focus();
      setMenuIsOpen(true);// Always open the dropdown when the component opens
    }
  }, [isOpen]);

  // Handle option change
  const handleSelectChange = (selectedOption: Ability | null) => {
    setSelectedOption(selectedOption);
    setSelectedDesc(selectedOption?.desc || 'No description available.');
    setMenuIsOpen(false); // Close the dropdown after selecting an option
  };

  // Ensure the dropdown closes when a selection is made
  const handleSelect = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (selectedOption) {
      onSelect(selectedOption);
      setMenuIsOpen(false); // Close the dropdown when an option is selected
    }
  };

  const handleCancel = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onCancel();
    setMenuIsOpen(false); // Close dropdown on cancel
  };

  // Handle opening and closing of dropdown
  const handleMenuOpen = () => {
    setMenuIsOpen(true); // Open the dropdown when clicked
  };

  const handleMenuClose = () => {
    setMenuIsOpen(false); // Close the dropdown when clicked outside or selection is made
  };

  if (!isOpen) {
    return null;
  }

  // Custom styles for react-select
  const customStyles: StylesConfig<Ability, false> = {
    groupHeading: (base: any) => ({
      ...base,
      backgroundColor: '#f2f2f2',
      color: '#333',
      paddingLeft: '10px',
      padding: '5px',
      fontSize: '26px',
      fontWeight: 'bold',
      textTransform: 'none',
      textAlign: 'left',
    }),
    option: (styles: any, { isFocused, isSelected }: { isFocused: boolean; isSelected: boolean }) => {
      return {
        ...styles,
        paddingLeft: '40px',
        paddingTop: '0px',
        paddingBottom: '0px',
        backgroundColor: isFocused ? 'lightgray' : 'white',
        color: 'black',
        fontWeight: isSelected ? 'bold' : 'normal',
        textAlign: 'left',
      };
    },
    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
    control: (provided: any) => ({ ...provided, width: '400px' }),
  };

  return (
    <div className="ability-container edit-field">
      <div className="multi-select-panel ability-panel ability-selector" id="abilitySelector">
        <div>
          <label htmlFor="ability-name">Name:</label>
          <Select
            ref={selectRef}
            inputId="ability-name"
            value={selectedOption}
            onChange={handleSelectChange}
            options={groupedAndSortedOptions}
            menuIsOpen={menuIsOpen}
            onMenuOpen={handleMenuOpen} // Open the dropdown when clicked
            onMenuClose={handleMenuClose} // Close dropdown when clicked outside
            getOptionValue={(option) => option.label || option.name} // Ensure uniqueness
            getOptionLabel={(option) => option.label || option.name} // Use label, fallback to name
            styles={customStyles}
          />
        </div>
        <div style={{ marginTop: '10px' }}>
          <label>Description:</label>
          <div style={{ height: '180px', overflowY: 'auto', textAlign: 'left', margin: '10px 0' }}>
            <MarkdownText text={selectedDesc} mon={null} />
          </div>
        </div>
        <button onClick={handleSelect}>Select</button>
        <button onClick={handleCancel}>Cancel</button>
      </div>
    </div>
  );
};

export default AbilitySelector;
